const DEFAULT_IDLE_THRESHOLD_MS = 60 * 1000;
const TYPING_DURATION_THRESHOLD_MS = 5 * 1000; // 5 seconds
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const EVENT_ENDPOINT = `${API_BASE_URL}/interview/event`;

class ProctoringTracker {
  private sessionId: string;
  private idleThreshold: number;
  private _sendEvent: (payload: any) => Promise<void>;
  private _idleTimer: NodeJS.Timeout | null = null;
  private _mediaTracks: Set<MediaStreamTrack> = new Set();
  private _started: boolean = false;
  
  // Event pairing for duration-based events
  private _pendingEvents: Map<string, { startTime: number; type: string }> = new Map();
  
  // Typing detection
  private _typingStartTime: number | null = null;
  private _typingTimer: NodeJS.Timeout | null = null;
  private _lastKeyPressTime: number = 0;

  constructor(sessionId: string, options: any = {}) {
    this.sessionId = sessionId;
    this.idleThreshold = options.idleThreshold ?? DEFAULT_IDLE_THRESHOLD_MS;
    this._sendEvent = options.sendEvent || this._defaultSendEvent;
  }

  start() {
    if (this._started) {
      return;
    }

    this._started = true;
    
    window.addEventListener("blur", this._handleWindowBlur);
    window.addEventListener("focus", this._handleWindowFocus);
    document.addEventListener("visibilitychange", this._handleVisibilityChange);

    // Only track keydown for typing detection and idle reset (no mouse events)
    window.addEventListener("keydown", this._handleKeyDown, { passive: true });
    this._resetIdleTimer();
  }

  trackMediaStream(stream: MediaStream, options: { camera?: boolean; microphone?: boolean } = {}) {
    if (!stream) {
      return;
    }

    const { camera = true, microphone = true } = options;

    stream.getTracks().forEach((track) => {
      if (
        (track.kind === "video" && camera) ||
        (track.kind === "audio" && microphone)
      ) {
        track.addEventListener("ended", this._handleTrackEnded);
        this._mediaTracks.add(track);
      }
    });
  }

  stop() {
    if (!this._started) {
      return;
    }
    this._started = false;

    window.removeEventListener("blur", this._handleWindowBlur as any);
    window.removeEventListener("focus", this._handleWindowFocus as any);
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);
    window.removeEventListener("keydown", this._handleKeyDown);

    if (this._typingTimer) {
      clearTimeout(this._typingTimer);
      this._typingTimer = null;
    }

    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
      this._idleTimer = null;
    }

    // Flush any pending events (store them with duration up to now)
    this._pendingEvents.forEach((pending, eventType) => {
      const duration = Date.now() - pending.startTime;
      const durationSeconds = Math.round(duration / 1000);
      this.sendEvent(eventType, {
        durationSeconds,
        endedAbruptly: true,
        startTime: new Date(pending.startTime).toISOString(),
        endTime: new Date().toISOString(),
      });
    });
    this._pendingEvents.clear();

    this._mediaTracks.forEach((track) => {
      track.removeEventListener("ended", this._handleTrackEnded);
    });
    this._mediaTracks.clear();
  }

  private _handleWindowBlur = () => {
    this._startPairedEvent("tab_blur");
  };

  private _handleWindowFocus = () => {
    this._endPairedEvent("tab_blur", "tab_focus");
  };

  private _handleVisibilityChange = () => {
    if (document.hidden) {
      this._startPairedEvent("tab_hidden");
    } else {
      this._endPairedEvent("tab_hidden", "tab_visible");
    }
  };

  private _handleTrackEnded = (event: Event) => {
    const track = (event.target as MediaStreamTrack);
    // Camera/mic stopped events are single events (no clear "resumed" event to pair with)
    if (track?.kind === "video") {
      this.sendEvent("camera_stopped");
    } else if (track?.kind === "audio") {
      this.sendEvent("mic_stopped");
    }
  };

  private _handleKeyDown = (event: KeyboardEvent) => {
    // Reset idle timer on any keypress
    this._resetIdleTimer();
    
    // Track typing duration
    const now = Date.now();
    const timeSinceLastKey = now - this._lastKeyPressTime;
    
    // If more than 2 seconds since last key, consider it a new typing session
    if (timeSinceLastKey > 2000 || !this._typingStartTime) {
      this._typingStartTime = now;
      // Clear any existing timer
      if (this._typingTimer) {
        clearTimeout(this._typingTimer);
        this._typingTimer = null;
      }
    }
    
    this._lastKeyPressTime = now;
    
    // If we haven't set a timer yet and typing just started, set one for 5 seconds
    if (this._typingStartTime && !this._typingTimer) {
      this._typingTimer = setTimeout(() => {
        // Check if user is still typing (keypress within last 2 seconds)
        const timeSinceLastKeyCheck = Date.now() - this._lastKeyPressTime;
        if (timeSinceLastKeyCheck < 2000 && this._typingStartTime) {
          const typingDuration = Date.now() - this._typingStartTime;
          this.sendEvent("user_typing", {
            durationSeconds: Math.round(typingDuration / 1000),
          });
        }
        // Reset for next typing session
        this._typingStartTime = null;
        this._typingTimer = null;
      }, TYPING_DURATION_THRESHOLD_MS);
    }
  };

  private _resetIdleTimer() {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer);
    }

    this._idleTimer = setTimeout(() => {
      this.sendEvent("user_idle", {
        idleThresholdMs: this.idleThreshold,
      });
    }, this.idleThreshold);
  }

  // Start a paired event (e.g., tab_hidden) - don't store yet, just record start time
  private _startPairedEvent(eventType: string) {
    if (!this._pendingEvents.has(eventType)) {
      this._pendingEvents.set(eventType, {
        startTime: Date.now(),
        type: eventType,
      });
    }
  }

  // End a paired event and store with duration
  private _endPairedEvent(startEventType: string, endEventType: string) {
    const pending = this._pendingEvents.get(startEventType);
    if (pending) {
      const duration = Date.now() - pending.startTime;
      const durationSeconds = Math.round(duration / 1000);
      
      // Store event with duration
      this.sendEvent(startEventType, {
        durationSeconds,
        endedWith: endEventType,
        startTime: new Date(pending.startTime).toISOString(),
        endTime: new Date().toISOString(),
      });
      
      this._pendingEvents.delete(startEventType);
    } else {
      // If no pending event, just send the end event
      this.sendEvent(endEventType);
    }
  }

  async sendEvent(type: string, details: any = {}) {
    if (!this.sessionId) {
      return;
    }
    await this._sendEvent({
      sessionId: this.sessionId,
      type,
      details,
      timestamp: new Date().toISOString(),
    });
  }

  private async _defaultSendEvent(payload: any) {
    try {
      const response = await fetch(EVENT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Event sent
    } catch (error) {
      // Event send failed
    }
  }
}

export function createProctoringTracker(sessionId: string, options?: any) {
  return new ProctoringTracker(sessionId, options);
}

export default ProctoringTracker;

